/**
 * What the workspace agent carries as its own face: an uploaded photo wins over
 * a Lucide glyph picked in Squad, and `null` means it has neither and the caller
 * keeps its own fallback (the Skalx brand plate).
 *
 * The glyph lives under a single `settings.icon` key so Squad can set and clear
 * it atomically next to whatever else that free-form blob holds — hence the
 * parsing rather than trusting the shape.
 */
import { icons } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { AgentGlyphAgent } from './agentSnapshot'

const PHOTO_URL = /^https?:\/\//i

const EXPORT_NAME_TOKEN = /\d+x\d+|\d[a-z]*|[A-Z][a-z]*/g

const NAMES_THE_TOKENIZER_CANNOT_DERIVE: Record<string, string> = {
  Clock10: 'clock-10',
  Clock11: 'clock-11',
  Clock12: 'clock-12'
}

const toLucideName = (exportName: string) =>
  NAMES_THE_TOKENIZER_CANNOT_DERIVE[exportName] ??
  (exportName.match(EXPORT_NAME_TOKEN) ?? []).join('-').toLowerCase()

let catalog: ReadonlyMap<string, LucideIcon> | null = null

/** Resolves a stored kebab-case name back to its Lucide component. */
export const lucideIconByName = (name: string): LucideIcon | undefined => {
  if (!catalog) {
    catalog = new Map(
      Object.entries(icons).map(([exportName, Icon]) => [
        toLucideName(exportName),
        Icon
      ])
    )
  }

  return catalog.get(name)
}

export type AgentGlyphSource =
  | { kind: 'photo'; url: string }
  | { kind: 'icon'; name: string; color: string }

export const agentGlyphSource = (
  agent: AgentGlyphAgent | undefined | null
): AgentGlyphSource | null => {
  if (!agent) return null

  const url = agent.avatar_url ?? ''

  if (PHOTO_URL.test(url)) return { kind: 'photo', url }

  const settings = agent.settings

  if (typeof settings !== 'object' || settings === null) return null

  const stored = (settings as Record<string, unknown>).icon

  if (typeof stored !== 'object' || stored === null) return null

  const { name, color } = stored as { name?: unknown; color?: unknown }

  if (typeof name !== 'string' || !name) return null

  return {
    kind: 'icon',
    name,
    color: typeof color === 'string' ? color : ''
  }
}
